/**
 * Toolbar d'accessibilité pour les tableaux — utile en délibération.
 * Permet de :
 *   - Afficher/masquer les lignes verticales du tableau (meilleure séparation)
 *   - Augmenter/diminuer la taille de la police (zoom)
 *
 * Préférences persistées via uiSlice + localStorage.
 *
 * Usage :
 *   import { TableAccessibilityToolbar, useTableA11yStyles } from '...';
 *
 *   const { tableStyle, tableClass } = useTableA11yStyles();
 *   ...
 *   <TableAccessibilityToolbar />
 *   <table className={tableClass} style={tableStyle}>...</table>
 */
import { useDispatch, useSelector } from 'react-redux';
import { Grid3x3, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import type { RootState } from '../../store';
import { toggleTableBorders, setTableFontScale } from '../../store/slices/uiSlice';

const FONT_SCALES = [0.8, 0.9, 1.0, 1.15, 1.3, 1.5, 1.6];

export function TableAccessibilityToolbar() {
  const dispatch = useDispatch();
  const tableBorders = useSelector((s: RootState) => s.ui.tableBorders);
  const tableFontScale = useSelector((s: RootState) => s.ui.tableFontScale);

  const idx = FONT_SCALES.findIndex(s => Math.abs(s - tableFontScale) < 0.01);
  const safeIdx = idx >= 0 ? idx : FONT_SCALES.findIndex(s => s >= tableFontScale);

  const handleZoomIn = () => {
    const next = FONT_SCALES[Math.min(FONT_SCALES.length - 1, safeIdx + 1)] ?? tableFontScale;
    dispatch(setTableFontScale(next));
  };
  const handleZoomOut = () => {
    const prev = FONT_SCALES[Math.max(0, safeIdx - 1)] ?? tableFontScale;
    dispatch(setTableFontScale(prev));
  };
  const handleReset = () => {
    dispatch(setTableFontScale(1));
  };

  const percent = Math.round(tableFontScale * 100);

  return (
    <div className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 border border-gray-200 dark:border-gray-600"
      role="toolbar" aria-label="Accessibilité du tableau">
      <button
        onClick={() => dispatch(toggleTableBorders())}
        title={tableBorders ? 'Masquer les lignes verticales' : 'Afficher les lignes verticales'}
        className={`p-1.5 rounded transition-colors ${
          tableBorders
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        }`}
        aria-pressed={tableBorders}
      >
        <Grid3x3 size={14} />
      </button>

      <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-0.5" />

      <button
        onClick={handleZoomOut}
        disabled={safeIdx <= 0}
        title="Réduire la taille du texte"
        className="p-1.5 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ZoomOut size={14} />
      </button>
      <span className="text-[11px] font-mono font-semibold text-gray-700 dark:text-gray-200 min-w-[34px] text-center"
        title="Taille de police courante">
        {percent}%
      </span>
      <button
        onClick={handleZoomIn}
        disabled={safeIdx >= FONT_SCALES.length - 1}
        title="Augmenter la taille du texte"
        className="p-1.5 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ZoomIn size={14} />
      </button>
      {Math.abs(tableFontScale - 1) > 0.01 && (
        <button
          onClick={handleReset}
          title="Revenir à la taille normale (100%)"
          className="p-1.5 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          <RotateCcw size={12} />
        </button>
      )}
    </div>
  );
}

/**
 * Hook utilitaire — renvoie le style à appliquer sur les éléments <table>.
 * `baseSizeRem` : taille de police de base (ex 0.75 pour text-xs, 0.875 pour text-sm).
 */
export function useTableA11yStyles(baseSizeRem = 0.875) {
  const tableBorders = useSelector((s: RootState) => s.ui.tableBorders);
  const tableFontScale = useSelector((s: RootState) => s.ui.tableFontScale);
  return {
    tableStyle: {
      fontSize: `${baseSizeRem * tableFontScale}rem`,
    },
    tableClass: tableBorders ? 'a11y-bordered' : '',
    tableBorders,
    tableFontScale,
  };
}
